import React, { useState, useEffect } from 'react';
import { progressService, JourneyProgress, ProgressItem } from '../services/progressService';
import { useLocale } from '../context/LocaleContext';
import styles from '../styles/journeyOverview.module.css';

interface JourneyOverviewProps {
  user?: any;
  stepProgress?: Record<number, ProgressItem | null>;
  /** When provided, parent already fetched journey data — skip internal load. */
  journeyProgress?: JourneyProgress | null;
}

const JourneyOverview: React.FC<JourneyOverviewProps> = ({
  user,
  stepProgress,
  journeyProgress: journeyProgressProp,
}) => {
  const { t } = useLocale();
  const parentProvidesJourney = journeyProgressProp !== undefined;
  const [internalJourneyData, setInternalJourneyData] = useState<JourneyProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const journeyData = parentProvidesJourney ? journeyProgressProp : internalJourneyData;

  useEffect(() => {
    if (!user || parentProvidesJourney) return;
    loadJourneyData();
  }, [user, parentProvidesJourney]);

  const loadJourneyData = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await progressService.getJourneyProgress();
      setInternalJourneyData(data);
    } catch (err) {
      console.error('Error loading journey data:', err);
      setError(t('working_steps.journey.load_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!parentProvidesJourney && isLoading) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.loading}>{t('working_steps.journey.loading')}</div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (!journeyData) {
    return null;
  }

  const getStepStatus = (stepNumber: number): 'not-started' | 'in-progress' | 'completed' => {
    if (!user || !stepProgress) return 'not-started';

    const progress = stepProgress[stepNumber];
    if (!progress) return 'not-started';

    if (progress.completed) return 'completed';

    if (progress.data && progress.data.actions && Array.isArray(progress.data.actions)) {
      const hasCompletedAction = progress.data.actions.some((action: any) => action.completed);
      if (hasCompletedAction) return 'in-progress';
    }

    return 'not-started';
  };

  const gettingStartedStatus = getStepStatus(0);

  const getHighestWorkedStep = (): { stepNumber: number; status: 'completed' | 'in-progress' } | null => {
    if (!stepProgress) return null;

    let highestStep = 0;
    let highestStatus: 'completed' | 'in-progress' = 'not-started' as any;

    for (let step = 1; step <= 12; step++) {
      const status = getStepStatus(step);
      if (status === 'completed' || status === 'in-progress') {
        highestStep = step;
        highestStatus = status;
      }
    }

    return highestStep > 0 ? { stepNumber: highestStep, status: highestStatus } : null;
  };

  const allStepsCompleted = () => {
    if (!stepProgress) return false;
    for (let step = 1; step <= 12; step++) {
      if (getStepStatus(step) !== 'completed') return false;
    }
    return true;
  };

  const anyStepsStarted = () => {
    if (!stepProgress) return false;
    for (let step = 1; step <= 12; step++) {
      if (getStepStatus(step) !== 'not-started') return true;
    }
    return false;
  };

  const anyGettingStartedStarted = gettingStartedStatus !== 'not-started';

  if (allStepsCompleted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>{t('working_steps.journey.all_done')}</div>
      </div>
    );
  }

  if (!anyGettingStartedStarted && !anyStepsStarted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.encouragingMessage}>{t('working_steps.journey.start_gs')}</div>
      </div>
    );
  }

  if (gettingStartedStatus !== 'completed' && !anyStepsStarted()) {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>
          {gettingStartedStatus === 'in-progress'
            ? t('working_steps.journey.gs_in_progress')
            : t('working_steps.journey.gs_start')}
        </div>
      </div>
    );
  }

  if (!anyStepsStarted() && gettingStartedStatus === 'completed') {
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.encouragingMessage}>{t('working_steps.journey.gs_done')}</div>
      </div>
    );
  }

  const highestWorked = getHighestWorkedStep();
  if (highestWorked) {
    if (highestWorked.status === 'completed') {
      if (highestWorked.stepNumber === 12) {
        const incompleteSteps: number[] = [];
        for (let step = 1; step <= 12; step++) {
          if (getStepStatus(step) !== 'completed') {
            incompleteSteps.push(step);
          }
        }

        if (incompleteSteps.length > 0) {
          return (
            <div className={styles.journeyOverview}>
              <div className={styles.journeyMessage}>{t('working_steps.journey.step12_partial')}</div>
            </div>
          );
        }
        return (
          <div className={styles.journeyOverview}>
            <div className={styles.journeyMessage}>{t('working_steps.journey.all_done')}</div>
          </div>
        );
      }
      const nextStep = highestWorked.stepNumber + 1;
      return (
        <div className={styles.journeyOverview}>
          <div className={styles.journeyMessage}>
            {t('working_steps.journey.step_done', {
              n: highestWorked.stepNumber,
              next: nextStep,
            })}
          </div>
        </div>
      );
    }
    return (
      <div className={styles.journeyOverview}>
        <div className={styles.journeyMessage}>
          {t('working_steps.journey.step_in_progress', { n: highestWorked.stepNumber })}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.journeyOverview}>
      <div className={styles.journeyMessage}>{t('working_steps.journey.started')}</div>
    </div>
  );
};

export default JourneyOverview;
